import type { FullActivityItem } from '@customTypes/types';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Badge, Box, Stack, Text } from '@mantine/core';

const DraggableTask = ({
  task,
  columnTitle,
  onSelectItem,
  RenderItem
}: {
  task: FullActivityItem;
  columnTitle: string;
  onSelectItem: (activityItem: FullActivityItem) => void;
  RenderItem: React.ComponentType<{
    task: FullActivityItem;
    onClick?: () => void;
    dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  }>;
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${task.workItemType}-${task.workItemId}`,
    data: {
      task,
      columnTitle
    }
  });

  const style = {
    transform: isDragging ? undefined : (transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined),
    opacity: isDragging ? 0 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    pointerEvents: isDragging ? ('none' as const) : ('auto' as const)
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <RenderItem
        dragHandleProps={{ ...attributes, ...listeners }}
        task={task}
        onClick={() => onSelectItem(task)}
      />
    </Box>
  );
};

export const BoardCol = ({
  colTitle,
  data,
  onSelectItem,
  renderItem: RenderItem
}: {
  colTitle: string;
  data: FullActivityItem[];
  onSelectItem: (activityItem: FullActivityItem) => void;
  renderItem: React.ComponentType<{
    task: FullActivityItem;
    onClick?: () => void;
    dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  }>;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: colTitle
  });

  return (
    <Stack
      ref={setNodeRef}
      gap='md'
      style={{
        padding: '20px',
        borderRadius: '12px',
        backgroundColor: isOver ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(10px)',
        border: isOver ? '2px solid #228be6' : '1px solid rgba(255, 255, 255, 0.5)',
        minWidth: '280px',
        boxShadow: isOver ? '0 8px 16px rgba(0, 0, 0, 0.15)' : '0 4px 6px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease'
      }}
    >
      <Box>
        <Badge
          color='blue'
          size='lg'
          variant='light'
          styles={{
            label: {
              textTransform: 'capitalize',
              fontSize: '14px',
              fontWeight: 600
            }
          }}
        >
          {colTitle}
        </Badge>
        <Text c='dimmed' mt={4} size='xs'>
          {data.length} {data.length === 1 ? 'item' : 'items'}
        </Text>
      </Box>
      <Stack gap='sm'>
        {data.map((item: FullActivityItem) => (
          <DraggableTask
            key={`${item.workItemType}-${item.workItemId}`}
            columnTitle={colTitle}
            RenderItem={RenderItem}
            task={item}
            onSelectItem={onSelectItem}
          />
        ))}
      </Stack>
    </Stack>
  );
};
